<template>
  <content-subpage
    class="profile-content-subpage"
    :visible="visible"
    :titles="titles"
    :show-content-title="false"
    :has-parent-subpage="hasParentSubpage"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack()"
  >
    <template #main-content>
      <div class="main-content">
        <inline-loader v-if="false" />
        <template v-else>
          <div class="avatar-wrapper">
            <div class="avatar-displayed">
              <user-avatar
                :user-avatar-url="userAvatarFile"
                :user-id="userId"
                :open-profile-subpage-when-clicked="false"
              />
            </div>
            <div class="avatar-user-details">
              <h6>{{ name }}</h6>
              <p>{{ roleName }}</p>
              <p>{{ schoolName }}</p>
            </div>
          </div>
          <div class="profile-content">
            <div v-if="isTeacher" class="my-contribution">
              <h6 class="title">{{ t("profile_subpage_my_contribution") }}</h6>
              <div class="profile-contribution">
                <!-- Modules Submitted -->
                <profile-contribution-item
                  icon-name="Lesson24"
                  :contribution-label="t('profile_subpage_contribution_modules_submitted')"
                  :contribution-number-label="userContribution.moduleSubmittedCount"
                  :has-sub-item="true"
                  sub-icon-name="Upvote16"
                  :contribution-sub-label="t('profile_subpage_contribution_votes_received')"
                  :contribution-number-sub-label="userContribution.moduleVotesReceivedCount"
                  :has-external-link="true"
                  :external-link-clicked="handleModuleSubmittedLinkClicked"
                />

                <!-- Reviews Given -->
                <profile-contribution-item
                  icon-name="Comment24"
                  :contribution-label="t('profile_subpage_contribution_reviews_given')"
                  :contribution-number-label="userContribution.reviewsGivenCount"
                />

                <!-- Communities Joined -->
                <profile-contribution-item
                  icon-name="Community24"
                  :contribution-label="t('profile_subpage_contribution_communities_joined')"
                  :contribution-number-label="userContribution.communitiesJoinedCount"
                  :has-external-link="true"
                  :external-link-clicked="handleCommunitiesJoinedLinkClicked"
                />

                <!-- Votes Given -->
                <profile-contribution-item
                  icon-name="Upvote24"
                  :contribution-label="t('profile_subpage_contribution_votes_given')"
                  :contribution-number-label="userContribution.moduleVotesGivenCount"
                />
              </div>
            </div>

            <!-- Badges Listing -->
            <div class="my-digital-badges">
              <h6 class="title">{{ t("profile_subpage_digital_badges_header") }}</h6>
              <profile-badges :user-id="userId" />
            </div>
          </div>
        </template>
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import ProfileContributionItem from "@modules/profile/components/ProfileContributionItem.vue";
import profileApi from "@apis/profile.api";
import ProfileBadges from "@modules/profile/components/ProfileBadges.vue";
import JoinedCommunityListingContentSubpage from "@modules/student-group/components/JoinedCommunityListingContentSubpage.vue";
import appConfigs from "@configs/app";
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import RandomUtils from "@utils/random.utils.ts";
import { UserProfile, UserStats } from "@modules/profile/profile-types.ts";
import SecurityUtils from "@utils/security.utils.ts";

interface ProfileSubpageParams {
  baseTitle?: string[];
  hasParentSubpage?: boolean;
  userId: number | string;
  actionHidden?: () => void;
}

interface Props {
  params: ProfileSubpageParams;
}

const props = defineProps<Props>();

// Data
const userContribution = ref<UserStats>({});
const userProfile = ref<UserProfile>({});
const hasParentSubpage = ref(props.params.hasParentSubpage || false);
const parentSubpageName = ref("");
const dirtyId = ref(RandomUtils.randomId());

const { t } = useI18n();
const router = useRouter();
const contentSubpageHandler = useContentSubpageInstance();
const { passiveApiFailHandler } = useErrorHandler();
const {
  visible,
  titles,
  setVisible,
  actionHiddenCallBack,
  actionBackCallBack,
  actionHidden,
  actionBack,
  isComponentDirty,
  removeComponentDirty,
  promptComponentDirty,
} = useContentSubpage(props.params, dirtyId);

// Computed
const userId = computed(() => props.params.userId);

const name = computed(() => userProfile.value.name);

const schoolName = computed(() => userProfile.value.schoolName);

const isTeacher = computed(() => userProfile.value.hasTeacherRole);

const roleName = computed(() => {
  return isTeacher.value ? t("profile_subpage_role_teacher") : t("profile_subpage_role_student");
});

const userAvatarFile = computed(() => {
  const userAvatarFile =
    userProfile.value.avatarFilename && userProfile.value.avatarFilename.startsWith("default")
      ? `${appConfigs.s3ResourceDomain}/avatar/user/${
          userProfile.value.avatarFilename
        }.svg?t=${new Date().getTime()}`
      : `${appConfigs.s3ResourceDomain}/avatar/user/${userProfile.value.avatarFilename}/${
          userProfile.value.avatarFileVersion
        }/${userProfile.value.avatarFilename}.svg?t=${new Date().getTime()}`;
  return userAvatarFile;
});

const backLabel = computed(() => `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`);

// Methods
const handleModuleSubmittedLinkClicked = () => {
  const routeInfo = {
    name: "FindCommunityGallery",
    query: {
      authors: [userId.value],
    },
  };

  const route = router.resolve(routeInfo);
  SecurityUtils.openInNewTab(route.href);
};

const handleCommunitiesJoinedLinkClicked = () => {
  contentSubpageHandler.show(JoinedCommunityListingContentSubpage, {
    userId: userId.value,
  });
};

const fetchUserStats = async () => {
  if (!isTeacher.value) {
    return;
  }

  try {
    const res = await profileApi.fetchUserStats(userId.value);

    if (res.code !== "ok") {
      throw new Error("Failed to fetch user stats");
    }

    userContribution.value = res.data;
  } catch (error) {
    throw error;
  }
};

const fetchUserProfileInfo = async () => {
  try {
    const res = await profileApi.fetchUserProfileInfo(userId.value);

    if (res.code !== "ok") {
      throw new Error("Failed to fetch user profile info");
    }

    userProfile.value = res.data;
  } catch (error) {
    throw error;
  }
};

onMounted(async () => {
  if (hasParentSubpage.value) {
    parentSubpageName.value = titles.value.slice(-1)[0];
  }
  titles.value.push(t("page_title_profile"));

  try {
    await fetchUserProfileInfo();
    await fetchUserStats();
  } catch (error) {
    passiveApiFailHandler(error);
  }
});

defineExpose({
  params: props.params,
  setVisible,
  actionHiddenCallBack,
  actionBackCallBack,
  isComponentDirty,
  removeComponentDirty,
  promptComponentDirty,
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.profile-content-subpage {
  .avatar-wrapper {
    padding: $spacing-02 $spacing-02 $spacing-06;

    .user-avatar {
      width: 145px;
      height: 145px;
      cursor: default;
    }

    .avatar-user-details {
      text-align: center;

      h6 {
        @include carbon--type-style("productive-heading-02");
      }

      p {
        @include carbon--type-style("body-long-01");
        color: $text-02;
      }
    }
  }

  .profile-content {
    margin: 0 -#{$spacing-05} -#{$spacing-05};
    padding: $spacing-05 $spacing-06 $spacing-06;
    background-color: $ui-01;

    :is(.my-contribution, .my-digital-badges) {
      > .title {
        @include carbon--type-style("productive-heading-02");
        margin-bottom: $spacing-05;
      }
    }

    .my-contribution {
      & ~ .my-digital-badges {
        margin-top: $spacing-07;
      }
    }

    .my-digital-badges {
      > .title {
        margin-bottom: $spacing-06;
      }
    }
  }

  .profile-contribution {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
    grid-gap: $spacing-05;
  }
}
</style>
